/**
 * Consumes a Devices-view pending unit open for one repeatable step (#999 3.5 4b).
 *
 * Peeks until the target unit is present (lists may still be loading), then
 * opens the editor. Takes pending (arms return-to-Devices) only after
 * `isEditorOpen` is true — survives Strict Mode remount without consuming
 * pending before the editor sticks.
 */

import { useEffect, useRef } from 'react';

import {
  coerceDevicesUnitId,
  devicesEditorNavigation,
} from '../utils/devicesEditorNavigation';

import type { DeviceStepId } from '../utils/deviceRowMapper';

export function useDevicesEditorPendingOpen<T extends { id?: number | string }>(
  stepId: DeviceStepId | undefined,
  units: readonly T[],
  openEditor: (unit: T) => void,
  options?: {
    isEditorOpen?: boolean;
    currentEditorId?: number | string | null;
  },
): void {
  const isEditorOpen = options?.isEditorOpen ?? false;
  const hasCurrentEditorId = options
    ? Object.prototype.hasOwnProperty.call(options, 'currentEditorId')
    : false;
  const currentEditorId = coerceDevicesUnitId(options?.currentEditorId);
  const openAttemptedForId = useRef<number | null>(null);

  useEffect(() => {
    if (!stepId) {
      openAttemptedForId.current = null;
      return;
    }
    const pendingId = devicesEditorNavigation.peekPendingOpen(stepId);
    if (pendingId == null) {
      openAttemptedForId.current = null;
      return;
    }

    // Arm return only once the editor is actually open for this pending unit.
    if (
      isEditorOpen &&
      (!hasCurrentEditorId || currentEditorId === pendingId)
    ) {
      if (devicesEditorNavigation.peekPendingOpen(stepId) != null) {
        devicesEditorNavigation.takePendingOpen(stepId);
      }
      openAttemptedForId.current = null;
      return;
    }

    const unit = units.find(item => coerceDevicesUnitId(item.id) === pendingId);
    if (!unit) {
      // A non-empty list may still be cached or incrementally loading. Keep the
      // target until it arrives; RequestDetail abandons pending navigation when
      // the user leaves the equipment step or changes requests.
      return;
    }

    // Never replace a different open editor: it may contain unsaved fields or
    // attachments. Pending remains intact and opens after that editor closes.
    if (isEditorOpen && currentEditorId !== pendingId) {
      return;
    }

    if (openAttemptedForId.current === pendingId) {
      return;
    }
    openAttemptedForId.current = pendingId;
    openEditor(unit);
  }, [
    stepId,
    units,
    openEditor,
    isEditorOpen,
    hasCurrentEditorId,
    currentEditorId,
  ]);
}
